import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/useAuth';
import { sbpService, fetchSBPItemReport } from '../services/sbpService';
import { fetchSubcounties } from '../services/subcountyService';
import { getFinancialYearData } from '../utils/dateHelper'; // Standard Utility
import { LayoutDashboard, RefreshCcw, TrendingUp, Calendar, Clock } from 'lucide-react';
import { formatToLocalTime, formatCurrentMonthTitle, formatTodayTitle, getFYStartYear} from '../utils/dateHelper'; // For formatting last updated time
import { calculatePerformance, getHeatmapClass } from '../utils/analysisUtils'; // For performance calculations and heatmap classes
import { fetchRevenueTargets } from '../services/revTargetService'; // For fetching monthly targets
import { fetchSubCountyAnalysis } from '../services/analysisService';
import { fetchCountyAnalysis } from '../services/analysisService';
import { use } from 'react';

const SBPDashboard = () => {
    const { isRestricted, user } = useAuth();
    
    // 1. Initialize Financial Year using Utility
    const fyOptions = getFinancialYearData();
    const [selectedFY, setSelectedFY] = useState(fyOptions.current);

    // 2. Initialize Sub-County state
    const [subcounties, setSubcounties] = useState([]);
    const [selectedSC, setSelectedSC] = useState(''); // Default to user's sub-county if restricted
    
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    // State for detailed report data (for the monthly performance table)
     const [reportData, setReportData] = useState({}); // Local state to hold data for exports
     const [nextRefresh, setNextRefresh] = useState(10); // minutes remaining for next auto-refresh
    
     // Define the order of months for the SBP Monthly table
     const monthsInOrder = [
        'July', 'August', 'September', 'October', 'November', 'December',
        'January', 'February', 'March', 'April', 'May', 'June'
    ];
    const [categoryData, setCategoryData] = useState({});

    // Fetch sub-counties for the dropdown
    // Replace your existing SC useEffect with this
useEffect(() => {
    fetchSubcounties().then(data => {
        setSubcounties(data);
        // If restricted, force the selectedSC to the user's ID
        if (user?.sub_county_id) {
            setSelectedSC(user.sub_county_id);
        }
    });
}, [user?.sub_county_id]);

    const loadReportData = useCallback(async () => {
        try {
            const fyValue = typeof selectedFY === 'object' ? selectedFY.financial_year : selectedFY;
            const scValue = selectedSC === '' ? null : selectedSC; // Convert empty string to null for API
            //console.log(`Loading SBP Item Report for FY: ${fyValue}, SubCounty ID: ${scValue}`);  
            const data = await fetchSBPItemReport({ 
                financial_year: fyValue, 
                sub_county_id: scValue 
            });
            
            // Check if data is truly empty or just structured differently
            //console.log("Data keys found:", Object.keys(data));
            
            if (data && Object.keys(data).length > 0) {
                setCategoryData(data);
            } else {
                console.warn("API returned an empty data set.");
                setCategoryData({});
            }
        } catch (error) {
            console.error("Failed to load SBP items:", error);
        }
    }, [selectedFY, selectedSC]);

   const loadDashboard = useCallback(async () => {
        setLoading(true);
        try {
            // Procedure expects NULL for all subcounties
            const scId = selectedSC === '' ? null : Number(selectedSC);
            //console.log(`Loading SBP Dashboard for FY: ${selectedFY}, SubCounty ID: ${scId}`);
            const stats = await sbpService.getDashboardStats(selectedFY, scId);
            setData(stats);
        } catch (err) {
            console.error("Dashboard Load Error:", err);
        } finally {
            setLoading(false);
        }
    }, [selectedFY, selectedSC]);

    const loadMonthlyAnalysis = useCallback(async () => {
    // Convert "FY2025/2026" -> 2025
        const startYear = getFYStartYear(selectedFY);
        setLoading(true);
        try {
            // Use the existing service from analysisService.js
            let rawData;
            if (selectedSC) {
                // Fetch specific Sub-County data
                rawData = await fetchSubCountyAnalysis(startYear, selectedSC);
            } else {
                // Fetch Entire County aggregate data
                rawData = await fetchCountyAnalysis(startYear);
            }
            // Group data by stream as required for the SBP Monthly table
            const grouped = rawData.reduce((acc, row) => {
                if (!acc[row.rev_stream]) acc[row.rev_stream] = [];
                acc[row.rev_stream].push(row);
                return acc;
            }, {});

            setReportData(grouped);
        } catch (error) {
            console.error("Failed to fetch monthly analysis data", error);
        } finally {
            setLoading(false);
        }
    }, [selectedFY, selectedSC]);

    useEffect(() => {
        loadMonthlyAnalysis();
            loadDashboard();
            loadReportData();  
    }, [loadMonthlyAnalysis, loadDashboard, loadReportData, selectedFY, selectedSC]);

    useEffect(() => {
        // 1. Define the 10-minute interval (10 minutes * 60 seconds * 1000 milliseconds)
        const REFRESH_INTERVAL = 10 * 60 * 1000;

        const intervalId = setInterval(() => {
            console.log("Auto-refreshing SBP Dashboard data...");
            //loadDashboardData();
            loadMonthlyAnalysis();
            loadDashboard();
            loadReportData();
        }, REFRESH_INTERVAL);

        // 2. Cleanup: Clear the interval if the component unmounts
        return () => {
            clearInterval(intervalId);
            console.log("SBP Dashboard auto-refresh stopped.");
        };
        
    }, [loadDashboard, loadMonthlyAnalysis, loadReportData]);
    
    useEffect(() => {
        const timer = setInterval(() => {
            setNextRefresh((prev) => (prev <= 1 ? 10 : prev - 1));
        }, 60000); // Update every minute
        return () => clearInterval(timer);
    }, []);

    // Variables to track running totals
    let cumulativeActual = 0;
    let cumulativeTarget = 0;
    // Process the display data
    const processedMonthlyData = monthsInOrder.map(month => {
        const sbpStreamData = reportData['SBP'] || [];
        const record = sbpStreamData.find(r => r.calendar_month === month);

        const actual = record ? Number(record.amount_fy0 || 0) : 0;
        const target = record ? Number(record.monthly_target || 0) : 0;

        // Update running totals
        cumulativeActual += actual;
        cumulativeTarget += target;

        return {
            month,
            actual,
            target,
            performance: calculatePerformance(actual, target),
            cumulativeActual,
            cumulativeTarget,
            cumulativePerformance: calculatePerformance(cumulativeActual, cumulativeTarget)
        };
});

    //const lastUpdateDisplay = data?.lastUpdated ? formatToLocalTime(data.lastUpdated) : 'Waiting for sync...';
    
    const subCountyName = selectedSC ? subcounties.find(sc => String(sc.id) === String(selectedSC))?.name || 'Selected Sub-County'
    : 'Entire County';
    
    if (loading && !data) return <div className="p-10 text-center">Loading SBP Insights...</div>;
    // Add this check right before your return statement
    if (loading || !data || Object.keys(reportData).length === 0) {
        return (
            <div className="flex h-screen items-center justify-center flex-col gap-4">
                <RefreshCcw className="animate-spin text-blue-600" size={40} />
                <p className="text-slate-500 font-bold animate-pulse uppercase tracking-widest">
                    Synchronizing {isRestricted ? 'Sub-County' : 'County'} Data...
                </p>
            </div>
        );
    }
    return (
        <div className="-mt-2 space-y-4 animate-fadeIn"> 
            {/* -mt-6 pulls the component up to meet the Layout Header exactly */}
            
            {/* Compacted Header & Filter Section */}
            <div className="bg-white px-8 py-4 rounded-b-2xl shadow-sm border-x border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex flex-col">
                    <h1 className="text-xl font-black text-slate-800 flex items-center flex-wrap leading-tight">
                        <LayoutDashboard className="mr-3 text-blue-600" size={22} /> 
                        SBP Performance
                        <span className="ml-3 text-emerald-600 text-sm font-bold">[{subCountyName}]</span>
                        <span className="ml-2 text-blue-500 text-sm font-bold">[{selectedFY}]</span>
                    </h1>
                    <span className="text-sm font-bold tracking-tighter mt-0.5">
                        Updated: <span className="text-slate-600 text-red-600">{data?.lastUpdated ? formatToLocalTime(data.lastUpdated) : '---'}</span>
                    </span>
                </div>
                
                <span className="text-[12px] font-black text-slate-700 uppercase">
                    [ Next Dashboard Refresh is in: <span className="text-red-500">{nextRefresh} {nextRefresh !== 1 ? 'minutes' : 'minute'}</span> ]
                </span>

                <div className="flex items-center space-x-4">
                {/* FY Selection Pill */}
                    <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 transition-all focus-within:ring-2 focus-within:ring-blue-100">
                        <span className="text-[10px] font-black text-slate-400 uppercase mr-3">FY Period</span>
                        <select 
                            value={selectedFY} 
                            onChange={(e) => setSelectedFY(e.target.value)}
                            className="bg-transparent text-sm font-black text-slate-700 outline-none border-none p-0 cursor-pointer min-w-[100px]"
                        >
                            <option value={fyOptions.previous}>{fyOptions.previous}</option>
                            <option value={fyOptions.current}>{fyOptions.current}</option>
                            <option value={fyOptions.next}>{fyOptions.next}</option>
                        </select>
                    </div>

                    {/* Sub-County Selection Pill */}
                   <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                        <span className="text-[10px] font-black text-slate-400 uppercase mr-3">Region</span>
                        <select 
                            value={selectedSC} 
                            onChange={(e) => setSelectedSC(e.target.value)}
                            disabled={!!user?.sub_county_id}
                            className="bg-transparent text-sm font-bold text-slate-700 outline-none border-none cursor-pointer min-w-[140px] disabled:opacity-70"
                        >
                            {/* Only show "Entire County" if the user is not assigned to a specific sub-county */}
                            {!user?.sub_county_id && <option value="">Entire County</option>}
                            {subcounties.map(sc => (
                                <option key={sc.id} value={sc.id}>{sc.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Action Button */}
                    <button 
                        onClick={loadDashboard} 
                        className="p-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-md shadow-blue-100 transition-all active:scale-95"
                        title="Refresh Data"
                    >
                        <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>

            {/* Top Row: Business Volume & Real-time Revenue */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 px-1">
                <StatCard 
                    title="Total Businesses" 
                    value={data?.allBusinesses} 
                    subValue={`New: ${data?.newBizH1 ? data?.newBizH1.toLocaleString() : 0} (H1) | ${data?.newBizH2 ? data?.newBizH2.toLocaleString() : 0} (H2)`} 
                    icon={<TrendingUp />} 
                    color="blue" 
                />
                {/* Today's Revenue Card */}
                <StatCard 
                    title={`Today's Revenue (${data?.lastUpdated ? formatTodayTitle(data.lastUpdated) : '---'})`} 
                    value={data?.todayRev} 
                    subValue={data?.todayRev === 0 ? `0 Invoices Paid` : `${data?.todayCount} Invoices Paid`} 
                    icon={<Clock size={16} />} 
                    color="emerald" 
                    isMoney 
                />

                {/* Monthly Revenue Card */}
                <StatCard 
                    title={`This Month (${data?.lastUpdated ? formatCurrentMonthTitle(data.lastUpdated) : '---'})`} 
                    value={data?.monthRev} 
                    subValue={data?.monthRev === 0 ? `0 Invoices Paid` : `${data?.monthCount} Invoices Paid`} 
                    icon={<Calendar size={16} />} 
                    color="indigo" 
                    isMoney 
                />
                <StatCard 
                    title={`FY Realized [${selectedFY}]`} 
                    value={data?.paidRevTotal} 
                    subValue={data?.paidRevTotal === 0 ? `0 Invoices Paid` : `${data?.paidCountTotal} Total Invoices Paid`} 
                    icon={<TrendingUp />} 
                    color="blue" 
                    isMoney 
                />
            </div>

            {/* Detailed Performance Comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <PerformanceTable 
                    title="Permit Approval Pipeline"
                    rows={[
                        { label: 'Rejected/InComplete', total: data?.rjTotal, h1: data?.rjH1, h2: data?.rjH2 },
                        { label: 'Pending Biller', total: data?.pbTotal, h1: data?.pbH1, h2: data?.pbH2 },
                        { label: 'Pending RO', total: data?.roTotal, h1: data?.roH1, h2: data?.roH2 },
                        { label: 'Approved Total', total: data?.appTotal, h1: data?.appH1, h2: data?.appH2 },
                        { label: 'UnPaid / paid (approved)', total: `${data?.unpaidCountTotal} / ${data?.paidCountTotal}`, 
                        h1: `${data?.unpaidCountH1} / ${data?.paidCountH1}`, 
                        h2: `${data?.unpaidCountH2} / ${data?.paidCountH2}` },
                    ]}
                />
                
                <PerformanceTable 
                    title="Revenue Collection (FY)"
                    isMoney
                    rows={[
                        { label: 'Paid Revenue', color: 'text-green-600', total: data?.paidRevTotal, h1: data?.paidRevH1, h2: data?.paidRevH2 },
                        { label: 'Pending Revenue', color: "text-red-600", total: data?.unpaidRevTotal, h1: data?.unpaidRevH1, h2: data?.unpaidRevH2 },
                    ]}
                />
            </div>
            <MonthlySBPTable 
                data={processedMonthlyData} 
                selectedFY={selectedFY}
                subCountyName={subCountyName} 
            />
            <div className="bg-slate-50 px-6 py-4 border-b flex items-center justify-between">
                <h3 className="font-bold text-slate-700 flex items-center">
                    <Calendar className="mr-2 text-green-500" size={16} />
                    Single Business Permit Revenue + Additional invoice items Revenue For : 
                    <span className="ml-3 text-emerald-600 text-sm font-bold">[{subCountyName}]</span>
                    <span className="ml-2 text-blue-500 text-sm font-bold">[{selectedFY}]</span>
                </h3>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">July - June Cycle</span>
            </div>
            {/* Insert this below the Monthly SBP Revenue Performance Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                {Object.entries(categoryData).map(([category, items]) => {

                    return (
                        <div key={category} className="bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col h-[400px]">
                            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 shrink-0">
                                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">{category}</h3>
                            </div>
                            
                            {/* Scrollable area starts here */}
                            {/* Inside the category mapping loop */}
                            <div className="flex flex-col h-[350px] border border-gray-200 rounded-lg overflow-hidden">
                                <div className="overflow-y-auto relative">
                                    <table className="min-w-full border-separate border-spacing-0">
                                        <thead className="sticky top-0 z-30 bg-white">
                                            <tr className="bg-gray-100">
                                                <th className="px-4 py-2 text-left text-xs font-bold border-b">Item</th>
                                                <th className="px-4 py-2 text-right text-xs font-bold border-b">H1</th>
                                                <th className="px-4 py-2 text-right text-xs font-bold border-b">H2</th>
                                                <th className="px-4 py-2 text-right text-xs font-bold border-b">Total</th>
                                            </tr>
                                            {/* Check for SUB-TOTAL item to display at the top */}
                                            {items.find(i => i.Item === 'SUB-TOTAL') && (
                                                <tr className="bg-blue-600 text-white sticky top-[33px] z-30 shadow-md">
                                                    <td className="px-4 py-2 text-xs font-bold border-b">TOTALS</td>
                                                    <td className="px-4 py-2 text-xs text-right font-bold border-b">
                                                        {Number(items.find(i => i.Item === 'SUB-TOTAL').H1_Value).toLocaleString()}
                                                    </td>
                                                    <td className="px-4 py-2 text-xs text-right font-bold border-b">
                                                        {Number(items.find(i => i.Item === 'SUB-TOTAL').H2_Value).toLocaleString()}
                                                    </td>
                                                    <td className="px-4 py-2 text-xs text-right font-bold border-b">
                                                        {Number(items.find(i => i.Item === 'SUB-TOTAL').Total_Value).toLocaleString()}
                                                    </td>
                                                </tr>
                                            )}
                                        </thead>
                                        <tbody className="bg-white">
                                            {items.filter(i => i.Item !== 'SUB-TOTAL').map((row, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50 border-b">
                                                    <td className="px-4 py-2 text-xs text-gray-700">{row.Item}</td>
                                                    <td className="px-4 py-2 text-xs text-right text-gray-600">{Number(row.H1_Value).toLocaleString()}</td>
                                                    <td className="px-4 py-2 text-xs text-right text-gray-600">{Number(row.H2_Value).toLocaleString()}</td>
                                                    <td className="px-4 py-2 text-xs text-right font-semibold text-gray-900">{Number(row.Total_Value).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const StatCard = ({ title, value, subValue, icon, color, isMoney }) => (
    /* Reduced padding from p-5 to px-4 py-3 */
    <div className={`bg-white px-4 py-3 rounded-xl border-l-4 border-${color}-500 shadow-sm flex flex-col justify-center`}>
        {/* Reduced margin-bottom from mb-2 to mb-1 */}
        <div className="flex justify-between items-center text-slate-400 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider">{title}</span>
            <div className={`text-${color}-500`}>{icon}</div>
        </div>
        
        {/* Adjusted text size for better vertical fit */}
        <div className="text-xl font-black text-slate-800 leading-tight">
            {isMoney ? `KES ${Number(value || 0).toLocaleString()}` : Number(value || 0).toLocaleString()}
        </div>
        
        {/* Reduced text size from text-sm to text-[11px] and margin-top to mt-0.5 */}
        {subValue && (
            <div className="text-[11px] text-slate-400 mt-0.5 font-semibold truncate">
                {subValue}
            </div>
        )}
    </div>
);

const PerformanceTable = ({ title, rows, isMoney }) => (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Reduced header padding from py-4 to py-2.5 */}
        <div className="bg-slate-50 px-6 py-2.5 border-b">
            <h3 className="font-bold text-slate-700 text-sm">{title}</h3>
        </div>
        <table className="w-full text-left text-xs">
            <thead className="text-slate-400 uppercase text-[9px] font-black tracking-wider bg-slate-50/30">
                <tr>
                    {/* Reduced th padding from py-3 to py-2 */}
                    <th className="px-6 py-2">Category</th>
                    <th className="px-6 py-2 text-center">July - Dec (H1)</th>
                    <th className="px-6 py-2 text-center">Jan - June (H2)</th>
                    <th className="px-6 py-2 text-right pr-10 text-blue-600">Total</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
                {rows.map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                        {/* Reduced td padding from py-4 to py-2 */}
                        <td className={`px-6 py-2 font-bold ${row.color || 'text-slate-700'}`}>
                            {row.label}
                            {row.label === 'Pending RO' && (
                                <span className="ml-2 text-[9px] font-medium text-amber-600 italic">
                                    (Biller Approved)
                                </span>
                            )} 
                        </td>
                        <td className={`px-6 py-2 text-center ${row.color || 'text-slate-500'}`}>
                            {isMoney ? `KES ${Number(row.h1).toLocaleString()}` : row.h1}
                        </td>
                        <td className={`px-6 py-2 text-center ${row.color || 'text-slate-500'}`}>
                            {isMoney ? `KES ${Number(row.h2).toLocaleString()}` : row.h2}
                        </td>
                        <td className={`px-6 py-2 text-right pr-10 font-black ${row.color || 'text-slate-800'}`}>
                            {isMoney ? `KES ${Number(row.total).toLocaleString()}` : row.total}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);
const MonthlySBPTable = ({ data, selectedFY, subCountyName, isLoading }) => (
    <div className="mt-6 bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden col-span-full relative">
        {/* Loading Overlay */}
        {isLoading && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
                <div className="flex flex-col items-center">
                    <RefreshCcw className="animate-spin text-blue-600 mb-2" size={24} />
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Syncing Monthly Data...</span>
                </div>
            </div>
        )}

        <div className="bg-slate-50 px-6 py-4 border-b flex items-center justify-between">
            <h3 className="font-bold text-slate-700 flex items-center">
                <Calendar className="mr-2 text-blue-500" size={16} />
                Monthly SBP Revenue Performance 
                <span className="ml-3 text-emerald-600 text-sm font-bold">[{subCountyName}]</span>
                <span className="ml-2 text-blue-500 text-sm font-bold">[{selectedFY}]</span>
            </h3>
			<span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">July - June Cycle</span>
        </div>
        
        <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-slate-50/50 text-slate-400 text-[10px] font-black uppercase tracking-widest">
                        <th className="px-6 py-4 text-left border-r w-44">Metric</th>
                        {data.map(m => <th key={m.month} className="px-4 py-4 text-center border-r">{m.month}</th>)}
                        <th className="px-6 py-4 text-center bg-blue-50 text-blue-600">Total(FYTD)</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {/* Actuals Row */}
                    <tr>
                        <td className="px-6 py-4 font-bold text-slate-700 border-r bg-slate-50/10">Actual (KES)</td>
                        {data.map((m, i) => (
                            <td key={i} className="px-4 py-4 text-center font-semibold text-slate-800 border-r">
                                {m.actual.toLocaleString()}
                            </td>
                        ))}
                        <td className="px-6 py-4 text-center font-black text-blue-700 bg-blue-50/30">
                            {data[data.length - 1]?.cumulativeActual.toLocaleString()}
                        </td>
                    </tr>
                    {/* Targets Row */}
                    <tr>
                        <td className="px-6 py-4 font-bold text-slate-600 border-r bg-slate-50/10 italic">Target (KES)</td>
                        {data.map((m, i) => (
                            <td key={i} className="px-4 py-4 font-semibold text-center text-slate-400 italic border-r">
                                {m.target.toLocaleString()}
                            </td>
                        ))}
                        <td className="px-6 py-4 text-center font-bold text-slate-500 bg-blue-50/30 italic">
                            {data[data.length - 1]?.cumulativeTarget.toLocaleString()}
                        </td>
                    </tr>
                    {/* Achievement Heatmap */}
                    <tr>
                        <td className="px-6 py-4 font-bold text-slate-700 border-r bg-slate-50/10">Achievement %</td>
                        {data.map((m, i) => (
                            <td key={i} className="px-4 py-4 text-center border-r">
                                <div className={`inline-block px-3 py-1 rounded-lg text-[11px] font-black ${getHeatmapClass(m.performance)}`}>
                                    {m.performance.toFixed(1)}%
                                </div>
                            </td>
                        ))}
                        <td className="px-6 py-4 text-center bg-blue-50/30">
                            <div className={`inline-block px-4 py-1.5 rounded-xl text-xs font-black shadow-sm ${getHeatmapClass(data[data.length - 1]?.cumulativePerformance)}`}>
                                {data[data.length - 1]?.cumulativePerformance.toFixed(1)}%
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
);
export default SBPDashboard;